/**
 * Alshallal Pastry Company HR System - Document Details Page
 * Phase 15: OCR-Assisted Document Data Extraction
 * 
 * Shows document details with tabs for Info and OCR results.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import {
  ArrowLeft,
  FileText,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Loader2,
  User,
  Calendar,
  Scan,
  Shield
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { isExpired, isExpiringSoon } from '@/lib/expiry';
import DocumentOCRTab from '@/components/documents/DocumentOCRTab';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle },
};

const STATUS_LABELS = {
  pending: { en: 'Pending', ar: 'قيد المراجعة' },
  approved: { en: 'Approved', ar: 'موافق عليه' },
  rejected: { en: 'Rejected', ar: 'مرفوض' },
};

const DocumentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, hasRole } = useAuth();
  const { t, language, isRTL } = useLanguage();

  // State
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab state - check URL param for 'ocr'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'details');

  // Review dialog
  const [reviewDialog, setReviewDialog] = useState({ open: false, action: null });
  const [reviewReason, setReviewReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // RBAC
  const canReview = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');
  const canUseOCR = hasRole('hr_manager', 'hr_admin', 'super_admin');

  // Fetch document
  const fetchDocument = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/documents/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDocument(response.data);
    } catch (err) {
      console.error('Failed to fetch document:', err);
      setError(err.response?.data?.detail?.error || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  // Handle download
  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/documents/${id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document?.file_name || 'document');
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed');
    }
  };

  // Handle review
  const handleReview = async () => {
    if (reviewReason.length < 10) return;

    try {
      setSubmitting(true);
      await axios.post(
        `${API_URL}/documents/${id}/review`,
        {
          status: reviewDialog.action,
          reason: reviewReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setReviewDialog({ open: false, action: null });
      setReviewReason('');
      fetchDocument();
    } catch (err) {
      console.error('Review failed:', err);
      setError(err.response?.data?.detail?.error || 'Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PPp');
    } catch {
      return dateStr;
    }
  };

  const getStatusLabel = (status) => {
    const labels = STATUS_LABELS[status];
    return labels ? (language === 'ar' ? labels.ar : labels.en) : status;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Error state
  if (error && !document) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {isRTL ? 'خطأ' : 'Error'}
            </h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={() => navigate(-1)}>
              {isRTL ? 'رجوع' : 'Go Back'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!document) return null;

  const StatusIcon = STATUS_STYLES[document.status]?.icon || Clock;
  const expired = isExpired(document);
  const expiringSoon = isExpiringSoon(document);

  return (
    <div className="space-y-6" data-testid="document-details-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{document.document_type_name}</h1>
              <Badge className={STATUS_STYLES[document.status]?.bg}>
                <StatusIcon className="h-3 w-3 me-1" />
                {getStatusLabel(document.status)}
              </Badge>
            </div>
            <p className="text-slate-600 mt-1">{document.file_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownload}>
            <Download className="h-4 w-4 me-2" />
            {t('download')}
          </Button>
          {canReview && document.status === 'pending' && (
            <>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setReviewDialog({ open: true, action: 'approved' })}
              >
                <CheckCircle className="h-4 w-4 me-2" />
                {isRTL ? 'اعتماد' : 'Approve'}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setReviewDialog({ open: true, action: 'rejected' })}
              >
                <XCircle className="h-4 w-4 me-2" />
                {isRTL ? 'رفض' : 'Reject'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="details" className="gap-2">
            <FileText className="h-4 w-4" />
            {isRTL ? 'التفاصيل' : 'Details'}
          </TabsTrigger>
          {canUseOCR && (
            <TabsTrigger value="ocr" className="gap-2">
              <Scan className="h-4 w-4" />
              {isRTL ? 'OCR' : 'OCR'}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Document Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {isRTL ? 'معلومات المستند' : 'Document Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'نوع المستند' : 'Document Type'}</Label>
                    <p className="font-medium">{document.document_type_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'اسم الملف' : 'File Name'}</Label>
                    <p className="font-medium truncate">{document.file_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'حجم الملف' : 'File Size'}</Label>
                    <p className="font-medium">
                      {document.file_size ? `${(document.file_size / 1024).toFixed(1)} KB` : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'نوع الملف' : 'MIME Type'}</Label>
                    <p className="font-medium">{document.mime_type || '-'}</p>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'تاريخ الإصدار' : 'Issue Date'}</Label>
                    <p className="font-medium">{formatDate(document.issue_date)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'}</Label>
                    <p className={cn(
                      "font-medium",
                      expired && "text-red-600",
                      expiringSoon && !expired && "text-amber-600"
                    )}>
                      {document.expiry_date ? (
                        <>
                          {formatDate(document.expiry_date)}
                          {expired && <AlertTriangle className="inline h-4 w-4 ms-1" />}
                        </>
                      ) : '-'}
                    </p>
                  </div>
                </div>

                {document.notes && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-xs text-slate-500">{isRTL ? 'ملاحظات' : 'Notes'}</Label>
                      <p className="mt-1 p-2 bg-slate-50 rounded text-sm">{document.notes}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Employee & Status Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {isRTL ? 'معلومات إضافية' : 'Additional Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'الموظف' : 'Employee'}</Label>
                    <p className="font-medium">{document.employee_name || document.employee_id}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'الحالة' : 'Status'}</Label>
                    <Badge className={STATUS_STYLES[document.status]?.bg}>
                      <StatusIcon className="h-3 w-3 me-1" />
                      {getStatusLabel(document.status)}
                    </Badge>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'تاريخ الرفع' : 'Uploaded On'}</Label>
                    <p className="font-medium">{formatDate(document.uploaded_at)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{isRTL ? 'تم الرفع بواسطة' : 'Uploaded By'}</Label>
                    <p className="font-medium">{document.uploaded_by || '-'}</p>
                  </div>
                </div>

                {document.status !== 'pending' && (
                  <>
                    <Separator />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">{isRTL ? 'تمت المراجعة في' : 'Reviewed On'}</Label>
                        <p className="font-medium">{formatDate(document.reviewed_at)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{isRTL ? 'تمت المراجعة بواسطة' : 'Reviewed By'}</Label>
                        <p className="font-medium">{document.reviewed_by || '-'}</p>
                      </div>
                    </div>
                    {document.review_reason && (
                      <div>
                        <Label className="text-xs text-slate-500">{isRTL ? 'سبب المراجعة' : 'Review Reason'}</Label>
                        <p className={cn(
                          "mt-1 p-2 rounded text-sm",
                          document.status === 'approved' ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                        )}>
                          {document.review_reason}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* OCR Tab */}
        {canUseOCR && (
          <TabsContent value="ocr">
            <DocumentOCRTab 
              documentId={id} 
              documentName={document.file_name}
              onRefresh={fetchDocument}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => !open && setReviewDialog({ open: false, action: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={reviewDialog.action === 'approved' ? 'text-green-600' : 'text-red-600'}>
              {reviewDialog.action === 'approved'
                ? (isRTL ? 'اعتماد المستند' : 'Approve Document')
                : (isRTL ? 'رفض المستند' : 'Reject Document')}
            </DialogTitle>
            <DialogDescription>
              {document.document_type_name} - {document.file_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                {reviewDialog.action === 'approved'
                  ? (isRTL ? 'سبب الموافقة (مطلوب)' : 'Reason for approval (required)')
                  : (isRTL ? 'سبب الرفض (مطلوب)' : 'Reason for rejection (required)')}
              </Label>
              <Textarea
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                placeholder={isRTL ? 'أدخل السبب (10 أحرف على الأقل)...' : 'Enter reason (at least 10 characters)...'}
                rows={4}
              />
              {reviewReason.length > 0 && reviewReason.length < 10 && (
                <p className="text-sm text-red-500">
                  {isRTL ? `${10 - reviewReason.length} أحرف متبقية` : `${10 - reviewReason.length} more characters needed`}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog({ open: false, action: null })}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleReview}
              disabled={reviewReason.length < 10 || submitting}
              className={reviewDialog.action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" /> {t('loading')}</>
              ) : (
                reviewDialog.action === 'approved' ? (isRTL ? 'اعتماد' : 'Approve') : (isRTL ? 'رفض' : 'Reject')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentDetailsPage;
